import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createRoot, Root } from 'react-dom/client';
import { Modal, Form, Input, Button, message, Tabs, Divider, ConfigProvider, App, Spin } from 'antd';
import {
  UserOutlined,
  LockOutlined,
  NumberOutlined,
  QrcodeOutlined,
  WechatOutlined,
  MobileOutlined,
  SafetyOutlined,
  LoadingOutlined,
  ReloadOutlined,
  CloseCircleOutlined,
  LinkOutlined,
} from '@ant-design/icons';
import { createStyles } from 'antd-style';
import zhCN from 'antd/locale/zh_CN';
import { login, currentUser, getWechatQrcode, register, getWechatQrcode4Login, login4Scan, googleLogin } from '@/services/auth';
import { GOOGLE_CLIENT_ID } from '@/constants/config';
import IconGoogle from '@/assets/icons/IconGoogle.svg';

const useStyles = createStyles(({ token, css }) => ({
  modal: css`
    .ant-modal-content {
      padding: 0;
      overflow: hidden;
    }
    .ant-modal-close {
      top: 16px;
      right: 16px;
    }
  `,
  container: css`
    display: flex;
    min-height: 420px;
  `,
  leftPanel: css`
    width: 280px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 32px;
    border-right: 1px solid ${token.colorBorderSecondary};
    background: ${token.colorBgLayout};
  `,
  rightPanel: css`
    flex: 1;
    padding: 32px 40px;
    display: flex;
    flex-direction: column;
  `,
  singlePanel: css`
    flex: 1;
    padding: 32px 48px;
    display: flex;
    flex-direction: column;
  `,
  panelTitle: css`
    font-size: 18px;
    font-weight: 600;
    text-align: center;
    margin-bottom: 8px;
    color: ${token.colorText};
  `,
  panelSubtitle: css`
    font-size: 13px;
    color: ${token.colorTextSecondary};
    text-align: center;
    margin-bottom: 24px;
  `,
  qrcode: css`
    width: 160px;
    height: 160px;
    border: 1px solid ${token.colorBorder};
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 16px;
    background: #fff;
  `,
  qrcodeHint: css`
    color: ${token.colorTextSecondary};
    font-size: 13px;
    text-align: center;
    line-height: 1.6;
  `,
  formContainer: css`
    flex: 1;
    display: flex;
    flex-direction: column;
  `,
  formInput: css`
    height: 44px;
    border-radius: 8px;
    .ant-input-prefix {
      margin-right: 12px;
      color: ${token.colorTextSecondary};
    }
  `,
  captchaRow: css`
    display: flex;
    gap: 12px;
  `,
  captchaInput: css`
    flex: 1;
  `,
  captchaBox: css`
    width: 100px;
    height: 44px;
    border: 1px solid ${token.colorBorder};
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    font-weight: 500;
    cursor: pointer;
    user-select: none;
    background: ${token.colorBgContainer};
    &:hover {
      border-color: ${token.colorPrimary};
    }
  `,
  submitBtn: css`
    height: 44px;
    border-radius: 8px;
    font-size: 15px;
  `,
  tabs: css`
    .ant-tabs-nav {
      margin-bottom: 24px;
    }
    .ant-tabs-nav::before {
      border-bottom: none;
    }
    .ant-tabs-tab {
      padding: 8px 0;
      font-size: 16px;
      font-weight: 500;
    }
    .ant-tabs-ink-bar {
      height: 3px;
      border-radius: 2px;
    }
  `,
  thirdPartySection: css`
    margin-top: auto;
    padding-top: 16px;
  `,
  thirdPartyDivider: css`
    margin: 16px 0;
    .ant-divider-inner-text {
      color: ${token.colorTextSecondary};
      font-size: 13px;
    }
  `,
  thirdPartyBtns: css`
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 24px;
  `,
  thirdPartyBtn: css`
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s;
    border: 1px solid ${token.colorBorder};
    background: ${token.colorBgContainer};
    &:hover {
      border-color: ${token.colorPrimary};
      color: ${token.colorPrimary};
    }
  `,
  thirdPartyBtnLoading: css`
    position: relative;
    pointer-events: none;
    opacity: 0.72;
  `,
  thirdPartyBtnSpinning: css`
    animation: googleLoginSpin 0.9s linear infinite;

    @keyframes googleLoginSpin {
      from {
        transform: rotate(0deg);
      }
      to {
        transform: rotate(360deg);
      }
    }
  `,
  thirdPartyHint: css`
    margin-top: 12px;
    font-size: 12px;
    color: ${token.colorTextSecondary};
    text-align: center;
    min-height: 18px;
  `,
  googleBtn: css`
    &:hover {
      border-color: #4285F4;
      background: #e8f0fe;
    }
  `,
  wechatBtn: css`
    color: #07c160;
    &:hover {
      border-color: #07c160;
      background: #f6ffed;
    }
  `,
  bindingSection: css`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    flex: 1;
  `,
  backLink: css`
    color: ${token.colorTextSecondary};
    cursor: pointer;
    font-size: 13px;
    margin-top: 16px;
    &:hover {
      color: ${token.colorPrimary};
    }
  `,
  qrcodeWrapper: css`
    position: relative;
    width: 160px;
    height: 160px;
  `,
  qrcodeOverlay: css`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.95);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    z-index: 1;
  `,
  overlayError: css`
    color: ${token.colorError};
  `,
  overlayText: css`
    font-size: 13px;
    color: ${token.colorTextSecondary};
    margin-top: 8px;
    text-align: center;
  `,
  refreshBtn: css`
    margin-top: 12px;
  `,
}));

// 生成数学验证码
const generateCaptcha = () => {
  const ops = ['+', '-'];
  const a = Math.floor(Math.random() * 10);
  const b = Math.floor(Math.random() * 10);
  const c = Math.floor(Math.random() * 10);
  const op1 = ops[Math.floor(Math.random() * 2)];
  const op2 = ops[Math.floor(Math.random() * 2)];
  const expression = `${a}${op1}${b}${op2}${c}`;
  // eslint-disable-next-line no-eval
  const result = eval(expression);
  return { expression, result };
};

type ViewMode = 'default' | 'wechat' | 'binding';

interface LoginModalContentProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  affCode?: string;
  defaultTab?: 'login' | 'register';
}

// 纯净的 Modal 内容组件，不依赖任何 UmiJS 上下文
const LoginModalContent: React.FC<LoginModalContentProps> = ({ open, onClose, onSuccess, affCode, defaultTab }) => {
  const { styles, cx } = useStyles();
  const { message: messageApi } = App.useApp();
  const [loginForm] = Form.useForm();
  const [registerForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [googleLoginLoading, setGoogleLoginLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'login' | 'register'>(defaultTab || 'login');
  const [viewMode, setViewMode] = useState<ViewMode>('default');
  const [captcha, setCaptcha] = useState(() => generateCaptcha());
  const [registerCaptcha, setRegisterCaptcha] = useState(() => generateCaptcha());

  // 注册流程相关状态
  const [qrcodeUrl, setQrcodeUrl] = useState<string>('');
  const [ticket, setTicket] = useState<string>('');
  const [qrcodeLoading, setQrcodeLoading] = useState(false);
  const [scanProcessing, setScanProcessing] = useState(false); // 扫码后请求处理中
  const [scanError, setScanError] = useState(false); // 扫码登录失败状态
  const [savedClientAccount, setSavedClientAccount] = useState<string>(''); // 保存的项目编号（用于注册失败回填）
  const eventSourceRef = useRef<EventSource | null>(null);
  const googleTokenClientRef = useRef<any>(null);
  const googleLoginLoadingRef = useRef(false);

  const refreshCaptcha = useCallback(() => {
    setCaptcha(generateCaptcha());
    loginForm.setFieldValue('captcha', '');
  }, [loginForm]);

  const refreshRegisterCaptcha = useCallback(() => {
    setRegisterCaptcha(generateCaptcha());
    registerForm.setFieldValue('captcha', '');
  }, [registerForm]);

  useEffect(() => {
    if (open) {
      loginForm.resetFields();
      registerForm.resetFields();
      setActiveTab(defaultTab || 'login');
      setViewMode('default');
      refreshCaptcha();
      refreshRegisterCaptcha();
      setQrcodeUrl('');
      setTicket('');
      setScanProcessing(false);
      setScanError(false);
      setSavedClientAccount('');
      setGoogleLoginLoading(false);
      // 如果有邀请码，设置到注册表单
      if (affCode) {
        registerForm.setFieldsValue({ affCode });
      }

      // 初始化 Google OAuth 弹窗登录
      setTimeout(() => {
        // @ts-ignore
        if (window.google?.accounts?.oauth2) {
          // @ts-ignore
          googleTokenClientRef.current = window.google.accounts.oauth2.initTokenClient({
            client_id: GOOGLE_CLIENT_ID,
            scope: 'openid email profile',
            callback: onGoogleLoginSuccess,
            error_callback: (error: any) => {
              googleLoginLoadingRef.current = false;
              setGoogleLoginLoading(false);
              const errorMsg = error?.message || error?.type || 'Google 登录失败，请重试！';
              messageApi.error(errorMsg);
            },
          });
        }
      }, 300);
    } else {
      // 关闭弹窗时清理 SSE 连接
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    }
  }, [open, loginForm, registerForm, refreshCaptcha, refreshRegisterCaptcha]);

  // 组件卸载时清理 SSE 连接
  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
  }, []);

  const handleLogin = async (values: any) => {
    if (Number(values.captcha) !== captcha.result) {
      messageApi.error('验证码错误');
      refreshCaptcha();
      return;
    }

    setLoading(true);
    try {
      const { data: token } = await login({
        clientAccount: values.clientAccount,
        username: values.username,
        password: values.password,
      });

      if (token) {
        localStorage.setItem('token', token);
      }

      messageApi.success('登录成功！');
      onSuccess?.();
    } catch (error: any) {
      const errorMsg = error?.response?.data?.message || error?.message || '登录失败，请重试！';
      messageApi.error(errorMsg);
      refreshCaptcha();
    } finally {
      setLoading(false);
    }
  };

  const onGoogleLoginSuccess = async (response: any) => {
    try {
      const accessToken = response?.access_token;
      if (!accessToken) {
        throw new Error('未获取到 Google 授权令牌');
      }

      const { data: token } = await googleLogin({
        accessToken,
        affCode,
      });

      if (token) {
        localStorage.setItem('token', token);
        messageApi.success('登录成功！');
        onSuccess?.();
      }
    } catch (error: any) {
      const errorMsg = error?.response?.data?.message || error?.message || '登录失败，请重试！';
      messageApi.error(errorMsg);
    } finally {
      googleLoginLoadingRef.current = false;
      setGoogleLoginLoading(false);
    }
  };

  const handleGoogleBtnClick = () => {
    if (googleLoginLoadingRef.current) {
      return;
    }

    if (googleTokenClientRef.current) {
      googleLoginLoadingRef.current = true;
      setGoogleLoginLoading(true);
      // @ts-ignore
      googleTokenClientRef.current.requestAccessToken({ prompt: 'select_account' });
    } else {
      messageApi.warning('Google 登录组件加载中或被屏蔽，请检查网络');
    }
  };

  const handleRegister = async (values: any) => {
    if (Number(values.captcha) !== registerCaptcha.result) {
      messageApi.error('验证码错误');
      refreshRegisterCaptcha();
      return;
    }

    // 保存项目编号，用于注册失败时回填
    setSavedClientAccount(values.clientAccount);

    setLoading(true);
    setQrcodeLoading(true);

    try {
      // 调用后端获取微信二维码
      const { data } = await getWechatQrcode({
        partnerKey: values.clientAccount,
        account: values.username,
        affCode: values.affCode,
      });

      if (!data || !data.qrcode || !data.ticket) {
        messageApi.error('获取二维码失败，请重试');
        return;
      }

      setQrcodeUrl(`data:image/png;base64,${data.qrcode}`);
      setTicket(data.ticket);
      setViewMode('binding');

      // 建立 SSE 连接等待扫码结果
      const sseUrl = `/edu3Api/sse/api/connect4Scan?ticket=${data.ticket}`;
      const eventSource = new EventSource(sseUrl);
      eventSourceRef.current = eventSource;

      // 监听扫码成功事件
      eventSource.addEventListener('scan-qrcode-ai-nexus', async (event) => {
        try {
          const result = JSON.parse(event.data);
          const scanData = result.value || result;
          if (scanData.openid && scanData.unionid) {
            // 扫码成功，关闭 SSE 连接
            eventSource.close();
            eventSourceRef.current = null;

            // 显示处理中状态
            setScanProcessing(true);

            try {
              // 调用注册接口
              await register({
                ticket: data.ticket,
                openid: scanData.openid,
                unionid: scanData.unionid,
              });

              messageApi.success('注册成功！');
              onSuccess?.();
            } catch (registerError: any) {
              // 注册失败，回到上一步并填充项目编号
              const errorMsg = registerError?.response?.data?.message || registerError?.message || '注册失败，请重试';
              messageApi.error(errorMsg);

              // 回到默认视图
              setViewMode('default');
              setActiveTab('register');
              // 填充项目编号
              registerForm.setFieldsValue({ clientAccount: values.clientAccount });
              refreshRegisterCaptcha();
            } finally {
              setScanProcessing(false);
            }
          }
        } catch (e) {
          console.error('SSE 消息解析失败:', e);
        }
      });

      eventSource.onerror = (error) => {
        console.error('SSE 连接错误:', error);
        eventSource.close();
        eventSourceRef.current = null;
        messageApi.error('扫码连接中断，请重试');
        // 回到默认视图并填充项目编号
        setViewMode('default');
        setActiveTab('register');
        registerForm.setFieldsValue({ clientAccount: values.clientAccount });
        refreshRegisterCaptcha();
      };

    } catch (error: any) {
      const errorMsg = error?.response?.data?.message || error?.message || '注册失败，请重试';
      messageApi.error(errorMsg);
      refreshRegisterCaptcha();
    } finally {
      setLoading(false);
      setQrcodeLoading(false);
    }
  };

  const handleWechatLogin = async () => {
    setViewMode('wechat');
    setQrcodeLoading(true);
    setQrcodeUrl('');
    setScanError(false);

    try {
      // 调用后端获取微信登录二维码
      const { data } = await getWechatQrcode4Login();

      if (!data || !data.qrcode || !data.ticket) {
        messageApi.error('获取二维码失败，请重试');
        setViewMode('default');
        return;
      }

      setQrcodeUrl(`data:image/png;base64,${data.qrcode}`);
      setTicket(data.ticket);

      // 建立 SSE 连接等待扫码结果
      const sseUrl = `/edu3Api/sse/api/connect4Scan?ticket=${data.ticket}`;
      const eventSource = new EventSource(sseUrl);
      eventSourceRef.current = eventSource;

      // 监听扫码成功事件
      eventSource.addEventListener('scan-qrcode-ai-nexus', async (event) => {
        try {
          const result = JSON.parse(event.data);
          const scanData = result.value || result;
          if (scanData.openid && scanData.unionid) {
            // 扫码成功，关闭 SSE 连接
            eventSource.close();
            eventSourceRef.current = null;

            // 显示处理中状态
            setScanProcessing(true);

            try {
              // 调用扫码登录接口
              const { data: token } = await login4Scan({
                openid: scanData.openid,
                unionid: scanData.unionid,
              });

              if (token) {
                localStorage.setItem('token', token);
              }

              messageApi.success('登录成功！');
              onSuccess?.();
            } catch (loginError: any) {
              // 登录失败，显示失败蒙层
              const errorMsg = loginError?.response?.data?.message || loginError?.message || '登录失败，请重试';
              messageApi.error(errorMsg);
              setScanError(true);
            } finally {
              setScanProcessing(false);
            }
          }
        } catch (e) {
          console.error('SSE 消息解析失败:', e);
        }
      });

      eventSource.onerror = (error) => {
        console.error('SSE 连接错误:', error);
        eventSource.close();
        eventSourceRef.current = null;
        messageApi.error('扫码连接中断，请重试');
        setScanError(true);
      };

    } catch (error: any) {
      const errorMsg = error?.response?.data?.message || error?.message || '获取二维码失败，请重试';
      messageApi.error(errorMsg);
      setViewMode('default');
    } finally {
      setQrcodeLoading(false);
    }
  };

  // 刷新微信登录二维码
  const handleRefreshWechatQrcode = async () => {
    setScanError(false);
    setQrcodeLoading(true);

    // 关闭旧的 SSE 连接
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }

    try {
      const { data } = await getWechatQrcode4Login();

      if (!data || !data.qrcode || !data.ticket) {
        messageApi.error('获取二维码失败，请重试');
        setScanError(true);
        return;
      }

      setQrcodeUrl(`data:image/png;base64,${data.qrcode}`);
      setTicket(data.ticket);

      // 重新建立 SSE 连接
      const sseUrl = `/edu3Api/sse/api/connect4Scan?ticket=${data.ticket}`;
      const eventSource = new EventSource(sseUrl);
      eventSourceRef.current = eventSource;

      eventSource.addEventListener('scan-qrcode-ai-nexus', async (event) => {
        try {
          const result = JSON.parse(event.data);
          const scanData = result.value || result;
          if (scanData.openid && scanData.unionid) {
            eventSource.close();
            eventSourceRef.current = null;

            setScanProcessing(true);

            try {
              const { data: token } = await login4Scan({
                openid: scanData.openid,
                unionid: scanData.unionid,
              });

              if (token) {
                localStorage.setItem('token', token);
              }

              messageApi.success('登录成功！');
              onSuccess?.();
            } catch (loginError: any) {
              const errorMsg = loginError?.response?.data?.message || loginError?.message || '登录失败，请重试';
              messageApi.error(errorMsg);
              setScanError(true);
            } finally {
              setScanProcessing(false);
            }
          }
        } catch (e) {
          console.error('SSE 消息解析失败:', e);
        }
      });

      eventSource.onerror = (error) => {
        console.error('SSE 连接错误:', error);
        eventSource.close();
        eventSourceRef.current = null;
        messageApi.error('扫码连接中断，请重试');
        setScanError(true);
      };

    } catch (error: any) {
      const errorMsg = error?.response?.data?.message || error?.message || '刷新二维码失败';
      messageApi.error(errorMsg);
      setScanError(true);
    } finally {
      setQrcodeLoading(false);
    }
  };

  const handleBackToDefault = () => {
    // 关闭 SSE 连接
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    setViewMode('default');
    setQrcodeUrl('');
    setTicket('');
  };

  const renderLeftPanel = () => {
    if (viewMode !== 'wechat') return null;
    return (
      <div className={styles.leftPanel}>
        <div className={styles.panelTitle}>微信扫码登录</div>
        <div className={styles.qrcodeWrapper}>
          <div className={styles.qrcode}>
            {qrcodeLoading ? (
              <Spin indicator={<LoadingOutlined style={{ fontSize: 40 }} spin />} />
            ) : qrcodeUrl ? (
              <img src={qrcodeUrl} alt="微信二维码" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
            ) : (
              <QrcodeOutlined style={{ fontSize: 100, color: '#d9d9d9' }} />
            )}
          </div>
          {/* 处理中蒙层 */}
          {scanProcessing && (
            <div className={styles.qrcodeOverlay}>
              <Spin indicator={<LoadingOutlined style={{ fontSize: 32 }} spin />} />
              <div className={styles.overlayText}>登录中...</div>
            </div>
          )}
          {/* 失败蒙层 */}
          {scanError && !scanProcessing && (
            <div className={styles.qrcodeOverlay}>
              <Button
                type="primary"
                shape="circle"
                icon={qrcodeLoading ? <LoadingOutlined spin /> : <ReloadOutlined />}
                size="large"
                onClick={handleRefreshWechatQrcode}
                disabled={qrcodeLoading}
                title="刷新二维码"
              />
              <div className={styles.overlayText}>点击刷新</div>
            </div>
          )}
        </div>
        <div className={styles.qrcodeHint}>打开微信<br />扫一扫授权登录</div>
      </div>
    );
  };

  const renderLoginForm = () => (
    <Form form={loginForm} onFinish={handleLogin} layout="vertical" requiredMark={false}>
      <Form.Item name="clientAccount" rules={[{ required: true, message: '请输入企业账号' }]}>
        <Input prefix={<NumberOutlined />} placeholder="企业账号" className={styles.formInput} />
      </Form.Item>
      <Form.Item name="username" rules={[{ required: true, message: '请输入用户名' }]}>
        <Input prefix={<UserOutlined />} placeholder="用户名" className={styles.formInput} />
      </Form.Item>
      <Form.Item name="password" rules={[{ required: true, message: '请输入密码' }]}>
        <Input.Password prefix={<LockOutlined />} placeholder="密码" className={styles.formInput} />
      </Form.Item>
      <Form.Item name="captcha" rules={[{ required: true, message: '请输入计算结果' }]}>
        <div className={styles.captchaRow}>
          <Input prefix={<SafetyOutlined />} placeholder="计算结果" className={cx(styles.formInput, styles.captchaInput)} />
          <div className={styles.captchaBox} onClick={refreshCaptcha} title="点击刷新">{captcha.expression}</div>
        </div>
      </Form.Item>
      <Form.Item style={{ marginBottom: 0 }}>
        <Button type="primary" htmlType="submit" block loading={loading} className={styles.submitBtn}>登录</Button>
      </Form.Item>
    </Form>
  );

  const renderRegisterForm = () => (
    <Form form={registerForm} onFinish={handleRegister} layout="vertical" requiredMark={false} initialValues={{ username: 'user', affCode }}>
      <Form.Item name="clientAccount" rules={[{ required: true, message: '请输入个人用户帐号' }]}>
        <Input prefix={<NumberOutlined />} placeholder="个人用户帐号" className={styles.formInput} />
      </Form.Item>
      <Form.Item name="username" style={{ display: 'none' }}>
        <Input prefix={<UserOutlined />} readOnly className={styles.formInput} style={{ backgroundColor: '#f5f5f5' }} />
      </Form.Item>
      {affCode && (
        <Form.Item name="affCode">
          <Input prefix={<LinkOutlined />} readOnly className={styles.formInput} style={{ backgroundColor: '#f5f5f5' }} />
        </Form.Item>
      )}
      <Form.Item name="captcha" rules={[{ required: true, message: '请输入计算结果' }]}>
        <div className={styles.captchaRow}>
          <Input prefix={<SafetyOutlined />} placeholder="计算结果" className={cx(styles.formInput, styles.captchaInput)} />
          <div className={styles.captchaBox} onClick={refreshRegisterCaptcha} title="点击刷新">{registerCaptcha.expression}</div>
        </div>
      </Form.Item>
      <Form.Item style={{ marginBottom: 0 }}>
        <Button type="primary" htmlType="submit" block loading={loading} className={styles.submitBtn}>注册</Button>
      </Form.Item>
    </Form>
  );

  const renderThirdPartyLogin = () => (
    <div className={styles.thirdPartySection}>
      <Divider className={styles.thirdPartyDivider}>其他登录方式</Divider>
      <div className={styles.thirdPartyBtns}>
        <div
          id="googleBtn"
          className={cx(
            styles.thirdPartyBtn,
            styles.googleBtn,
            googleLoginLoading && styles.thirdPartyBtnLoading,
          )}
          title={googleLoginLoading ? 'Google 登录处理中' : 'Google登录'}
          onClick={handleGoogleBtnClick}
          aria-disabled={googleLoginLoading}
        >
          {googleLoginLoading ? (
            <LoadingOutlined className={styles.thirdPartyBtnSpinning} style={{ fontSize: 18, color: '#4285F4' }} />
          ) : (
            <img src={IconGoogle} alt="Google" width={24} height={24} />
          )}
        </div>
        <div className={cx(styles.thirdPartyBtn, styles.wechatBtn)} onClick={handleWechatLogin} title="微信登录">
          <WechatOutlined style={{ fontSize: 24 }} />
        </div>
      </div>
      <div className={styles.thirdPartyHint}>{googleLoginLoading ? '正在处理 Google 授权，请稍候...' : ''}</div>
    </div>
  );

  const renderBindingView = () => (
    <div className={styles.singlePanel}>
      <div className={styles.bindingSection}>
        <div className={styles.panelTitle}>扫码注册</div>
        <div className={styles.panelSubtitle}>请使用微信扫描二维码完成注册</div>
        <div className={styles.qrcodeWrapper}>
          <div className={styles.qrcode}>
            {qrcodeLoading ? (
              <Spin indicator={<LoadingOutlined style={{ fontSize: 40 }} spin />} />
            ) : qrcodeUrl ? (
              <img src={qrcodeUrl} alt="微信二维码" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
            ) : (
              <QrcodeOutlined style={{ fontSize: 100, color: '#d9d9d9' }} />
            )}
          </div>
          {/* 处理中蒙层 */}
          {scanProcessing && (
            <div className={styles.qrcodeOverlay}>
              <Spin indicator={<LoadingOutlined style={{ fontSize: 32 }} spin />} />
              <div className={styles.overlayText}>注册中...</div>
            </div>
          )}
        </div>
        <div className={styles.qrcodeHint}>打开微信扫一扫<br />扫码后自动完成注册</div>
        <div className={styles.backLink} onClick={handleBackToDefault}>返回</div>
      </div>
    </div>
  );

  const renderWechatLoginHint = () => (
    <div className={styles.rightPanel}>
      <div className={styles.bindingSection}>
        <div className={styles.panelTitle}>扫码登录</div>
        <div className={styles.panelSubtitle}>请使用微信扫描左侧二维码登录</div>
        <div className={styles.qrcodeHint} style={{ marginTop: 24 }}>扫码成功后将自动登录</div>
        <div className={styles.backLink} onClick={handleBackToDefault}>返回账号登录</div>
      </div>
    </div>
  );

  const renderDefaultView = () => (
    <div className={styles.singlePanel}>
      <Tabs
        activeKey={activeTab}
        onChange={(key) => setActiveTab(key as 'login' | 'register')}
        className={styles.tabs}
        centered
        items={[
          {
            key: 'login',
            label: '登录',
            children: <div className={styles.formContainer}>{renderLoginForm()}{renderThirdPartyLogin()}</div>,
          },
          {
            key: 'register',
            label: '注册',
            children: <div className={styles.formContainer}>{renderRegisterForm()}</div>,
          },
        ]}
      />
    </div>
  );

  const renderContent = () => {
    if (viewMode === 'binding') return renderBindingView();
    if (viewMode === 'wechat') return <>{renderLeftPanel()}{renderWechatLoginHint()}</>;
    return renderDefaultView();
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={viewMode === 'wechat' ? 600 : 420}
      centered
      destroyOnClose
      className={styles.modal}
    >
      <div className={styles.container}>{renderContent()}</div>
    </Modal>
  );
};

// ============================================
// 命令式 API（类似 Modal.confirm）
// ============================================

let root: Root | null = null;
let container: HTMLDivElement | null = null;
let currentVisible = false;
let currentOptions: { affCode?: string; defaultTab?: 'login' | 'register' } = {};

function getContainer() {
  if (!container) {
    container = document.createElement('div');
    container.id = 'global-login-modal';
    document.body.appendChild(container);
  }
  return container;
}

function render(visible: boolean, options: { affCode?: string; defaultTab?: 'login' | 'register' } = {}) {
  currentVisible = visible;
  currentOptions = options;
  const containerEl = getContainer();

  if (!root) {
    root = createRoot(containerEl);
  }

  const handleClose = () => render(false);
  const handleSuccess = () => {
    render(false);
    // 移除 URL 中的 aff 参数
    const url = new URL(window.location.href);
    if (url.searchParams.has('aff')) {
      url.searchParams.delete('aff');
      window.history.replaceState({}, '', url.pathname + url.search);
    }
    // 延迟刷新，让用户能看到成功提示
    setTimeout(() => {
      window.location.reload();
    }, 1000);
  };

  root.render(
    <ConfigProvider locale={zhCN}>
      <App>
        <LoginModalContent
          open={visible}
          onClose={handleClose}
          onSuccess={handleSuccess}
          affCode={options.affCode}
          defaultTab={options.defaultTab}
        />
      </App>
    </ConfigProvider>
  );
}

/**
 * 全局登录弹窗 - 命令式 API
 *
 * @example
 * import { loginModal } from '@/components/LoginModal';
 *
 * // 显示登录弹窗
 * loginModal.show();
 *
 * // 显示注册弹窗并带邀请码
 * loginModal.show({ affCode: 'xxx', defaultTab: 'register' });
 *
 * // 隐藏登录弹窗
 * loginModal.hide();
 */
export const loginModal = {
  show(options?: { affCode?: string; defaultTab?: 'login' | 'register' }) {
    render(true, options);
  },
  hide() {
    render(false);
  },
  isVisible() {
    return currentVisible;
  },
};

// 默认导出组件（用于需要在 React 树中使用的场景）
export default LoginModalContent;
